"use client";

import {
    ArrowLeft,
    ArrowUp,
    Check,
    ChevronDown,
    FileText,
    FolderPlus,
    ImageIcon,
    Paperclip,
    Plug,
    Plus,
    Sparkles,
    Wand2,
    X,
} from "lucide-react";
import {
    useCallback,
    useEffect,
    useRef,
    useState,
    type ChangeEvent,
} from "react";
import type { PrismCatalog, PrismModelId } from "@/lib/prism/catalog";
import type { UsePrismStateReturn } from "@/modules/prism/hooks/usePrismState";
import { usePrismSpeechInput } from "@/modules/prism/hooks/usePrismSpeechInput";
import { PrismSpeechMicButton } from "@/modules/prism/ui/PrismSpeechMicButton";

export interface ComposerAttachment {
    id: string;
    name: string;
    kind: "image" | "text";
    mime: string;
    data: string;
    /** Data URL for image thumbnail / lightbox preview. */
    previewUrl?: string;
}

type PlusPage = "root" | "project" | "skills" | "connectors";

interface PrismComposerProps {
    prism: UsePrismStateReturn;
    catalog: PrismCatalog;
    loading: boolean;
    value: string;
    onValueChange: (v: string) => void;
    onSend: (text: string) => void;
    attachments: ComposerAttachment[];
    onAttachmentsChange: (a: ComposerAttachment[]) => void;
    activeSkillId: string | null;
    onActiveSkillChange: (id: string | null) => void;
    companyName?: string;
    enabledConnectorIds: string[];
    onEnabledConnectorIdsChange: (ids: string[]) => void;
    variant?: "hero" | "docked";
}

function displayAttachmentName(file: File): string {
    const generic = /^image\.(png|jpe?g|gif|webp)$/i.test(file.name);
    if (generic) {
        const ext = file.name.split(".").pop()?.toLowerCase() ?? "png";
        return `Pasted image.${ext}`;
    }
    return file.name;
}

function readFileAsAttachment(file: File): Promise<ComposerAttachment | null> {
    return new Promise((resolve) => {
        const isImage = file.type.startsWith("image/");
        const isText =
            file.type.startsWith("text/") ||
            /\.(txt|md|csv|json)$/i.test(file.name) ||
            file.type === "application/json";
        if (!isImage && !isText) {
            resolve(null);
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result as string;
            const name = displayAttachmentName(file);
            if (isImage) {
                const base64 = result.includes(",") ? result.split(",")[1] : result;
                const mime = file.type || "image/png";
                resolve({
                    id: crypto.randomUUID(),
                    name,
                    kind: "image",
                    mime,
                    data: base64,
                    previewUrl: `data:${mime};base64,${base64}`,
                });
            } else {
                resolve({
                    id: crypto.randomUUID(),
                    name,
                    kind: "text",
                    mime: "text/plain",
                    data: `\n\n--- ${name} ---\n${result}`,
                });
            }
        };
        reader.onerror = () => resolve(null);
        if (isImage) reader.readAsDataURL(file);
        else reader.readAsText(file);
    });
}

export function PrismComposer({
    prism,
    catalog,
    loading,
    value,
    onValueChange,
    onSend,
    attachments,
    onAttachmentsChange,
    activeSkillId,
    onActiveSkillChange,
    companyName,
    enabledConnectorIds,
    onEnabledConnectorIdsChange,
    variant = "docked",
}: PrismComposerProps) {
    const [plusOpen, setPlusOpen] = useState(false);
    const [plusPage, setPlusPage] = useState<PlusPage>("root");
    const [modelOpen, setModelOpen] = useState(false);
    const [skillModalOpen, setSkillModalOpen] = useState(false);
    const [projectName, setProjectName] = useState("");
    const [previewAttachment, setPreviewAttachment] = useState<ComposerAttachment | null>(null);

    const plusRef = useRef<HTMLDivElement>(null);
    const modelRef = useRef<HTMLDivElement>(null);
    const fileRef = useRef<HTMLInputElement>(null);
    const textRef = useRef<HTMLTextAreaElement>(null);

    const { state, setModel, addProject, addCustomSkill } = prism;
    const selectedModel =
        catalog.models.find((m) => m.id === state.selectedModelId) ?? catalog.models[1] ?? catalog.models[0];

    const builtinSkills = catalog.skills;
    const customSkills = state.customSkills;
    const activeSkill =
        builtinSkills.find((s) => s.id === activeSkillId) ??
        customSkills.find((s) => s.id === activeSkillId);

    useEffect(() => {
        function onDocClick(e: MouseEvent) {
            if (plusRef.current && !plusRef.current.contains(e.target as Node)) setPlusOpen(false);
            if (modelRef.current && !modelRef.current.contains(e.target as Node)) setModelOpen(false);
        }
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, []);

    useEffect(() => {
        function onKey(e: KeyboardEvent) {
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "u") {
                e.preventDefault();
                fileRef.current?.click();
            }
        }
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, []);

    const handleFiles = useCallback(
        async (e: ChangeEvent<HTMLInputElement>) => {
            const files = Array.from(e.target.files ?? []);
            if (!files.length) return;
            const parsed = (await Promise.all(files.map(readFileAsAttachment))).filter(
                (a): a is ComposerAttachment => a !== null,
            );
            onAttachmentsChange([...attachments, ...parsed].slice(0, 5));
            setPlusOpen(false);
            if (fileRef.current) fileRef.current.value = "";
        },
        [attachments, onAttachmentsChange],
    );

    // Paste images / files directly into the composer (Ctrl/Cmd+V).
    const handlePaste = useCallback(
        async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
            const items = Array.from(e.clipboardData?.items ?? []);
            const files = items
                .filter((it) => it.kind === "file")
                .map((it) => it.getAsFile())
                .filter((f): f is File => f !== null);
            if (!files.length) return; // plain-text paste: let the textarea handle it
            e.preventDefault();
            const parsed = (await Promise.all(files.map(readFileAsAttachment))).filter(
                (a): a is ComposerAttachment => a !== null,
            );
            if (parsed.length) {
                onAttachmentsChange([...attachments, ...parsed].slice(0, 5));
            }
        },
        [attachments, onAttachmentsChange],
    );

    const pickSkill = useCallback(
        (id: string, promptTemplate: string) => {
            onActiveSkillChange(id);
            const filled = promptTemplate
                .replace("{company}", companyName ?? "the company")
                .replace("{theme}", "")
                .replace("{company}", "");
            onValueChange(filled);
            setPlusOpen(false);
            setTimeout(() => textRef.current?.focus(), 0);
        },
        [onActiveSkillChange, onValueChange, companyName],
    );

    const submit = useCallback(() => {
        const text = value.trim();
        if (!text || loading) return;
        onSend(text);
    }, [value, loading, onSend]);

    const canSend = value.trim().length > 0 && !loading;

    const speech = usePrismSpeechInput({
        value,
        onValueChange,
        disabled: loading,
        available: catalog.speechInput?.available ?? false,
    });

    return (
        <div className="w-full">
            <input
                ref={fileRef}
                type="file"
                multiple
                accept="image/*,.txt,.md,.csv,.json,text/*"
                className="hidden"
                onChange={handleFiles}
            />

            <div
                className={`relative rounded-[1.75rem] border border-[var(--border-hairline)] bg-surface-0 shadow-[var(--shadow-pop)] transition-shadow focus-within:shadow-[0_4px_32px_rgba(79,70,229,0.12)] ${
                    variant === "hero" ? "px-4 pt-4 pb-3" : "px-3 pt-3 pb-2.5"
                }`}
            >
                {/* Active skill + attachment chips */}
                {(activeSkill || attachments.length > 0) && (
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                        {activeSkill ? (
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-bg text-accent px-2.5 py-1 text-xs font-medium ring-1 ring-accent-line">
                                <Sparkles className="w-3 h-3" />
                                {activeSkill.name}
                                <button
                                    type="button"
                                    onClick={() => onActiveSkillChange(null)}
                                    className="hover:text-accent"
                                    aria-label="Clear skill"
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            </span>
                        ) : null}
                        {attachments.map((a) =>
                            a.kind === "image" && a.previewUrl ? (
                                <span
                                    key={a.id}
                                    className="relative inline-flex items-center gap-1 rounded-lg bg-surface-1 p-1 ring-1 ring-[var(--border-hairline)]"
                                >
                                    <button
                                        type="button"
                                        onClick={() => setPreviewAttachment(a)}
                                        className="block h-11 w-11 overflow-hidden rounded-md bg-surface-2 hover:ring-2 hover:ring-accent-line transition-shadow"
                                        title={`Preview ${a.name}`}
                                        aria-label={`Preview ${a.name}`}
                                    >
                                        {/* eslint-disable-next-line @next/next/no-img-element */}
                                        <img
                                            src={a.previewUrl}
                                            alt={a.name}
                                            className="h-full w-full object-cover"
                                        />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            onAttachmentsChange(attachments.filter((x) => x.id !== a.id))
                                        }
                                        className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-surface-0 text-ink-2 shadow ring-1 ring-[var(--border-hairline)] hover:text-ink-0"
                                        aria-label="Remove attachment"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </span>
                            ) : (
                                <span
                                    key={a.id}
                                    className="inline-flex items-center gap-1.5 rounded-full bg-surface-1 text-ink-1 px-2.5 py-1 text-xs ring-1 ring-[var(--border-hairline)]"
                                >
                                    {a.kind === "image" ? (
                                        <ImageIcon className="w-3 h-3" />
                                    ) : (
                                        <FileText className="w-3 h-3" />
                                    )}
                                    <span className="max-w-[120px] truncate">{a.name}</span>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            onAttachmentsChange(attachments.filter((x) => x.id !== a.id))
                                        }
                                        className="hover:text-ink-0"
                                        aria-label="Remove attachment"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </span>
                            ),
                        )}
                    </div>
                )}

                <textarea
                    ref={textRef}
                    value={value}
                    onChange={(e) => onValueChange(e.target.value)}
                    onPaste={handlePaste}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            submit();
                        }
                    }}
                    rows={variant === "hero" ? 2 : 1}
                    placeholder="How can I help you today?"
                    className="w-full resize-none bg-transparent px-2 text-[15px] leading-relaxed text-ink-0 placeholder:text-ink-3 focus:outline-none max-h-44"
                />

                {speech.error ? (
                    <p className="px-2 mt-1 text-xs text-bad" role="alert">
                        {speech.error}
                    </p>
                ) : null}

                <div className="flex items-center justify-between gap-2 mt-1">
                    {/* Plus button + menu */}
                    <div className="flex items-center gap-1">
                    <div className="relative" ref={plusRef}>
                        <button
                            type="button"
                            onClick={() => {
                                setPlusOpen((o) => !o);
                                setPlusPage("root");
                            }}
                            className="flex h-9 w-9 items-center justify-center rounded-full text-ink-2 hover:bg-surface-1 hover:text-ink-0 transition-colors"
                            aria-label="Add context"
                        >
                            <Plus className="w-5 h-5" />
                        </button>

                        {plusOpen ? (
                            <div className="absolute bottom-11 left-0 z-30 w-72 rounded-2xl border border-[var(--border-hairline)] bg-surface-0 shadow-xl py-1.5 text-sm">
                                {plusPage === "root" ? (
                                    <>
                                        <MenuRow
                                            icon={<Paperclip className="w-4 h-4" />}
                                            label="Add files or photos"
                                            hint="Ctrl+U"
                                            onClick={() => fileRef.current?.click()}
                                        />
                                        <MenuRow
                                            icon={<FolderPlus className="w-4 h-4" />}
                                            label="Add to project"
                                            chevron
                                            onClick={() => setPlusPage("project")}
                                        />
                                        <div className="my-1 h-px bg-[var(--border-hairline)]" />
                                        <MenuRow
                                            icon={<Sparkles className="w-4 h-4" />}
                                            label="Skills"
                                            chevron
                                            onClick={() => setPlusPage("skills")}
                                        />
                                        <MenuRow
                                            icon={<Plug className="w-4 h-4" />}
                                            label="Connectors"
                                            chevron
                                            onClick={() => setPlusPage("connectors")}
                                        />
                                    </>
                                ) : null}

                                {plusPage === "project" ? (
                                    <div className="px-1">
                                        <SubHeader title="Add to project" onBack={() => setPlusPage("root")} />
                                        <div className="max-h-44 overflow-y-auto">
                                            {state.projects.length === 0 ? (
                                                <p className="px-3 py-2 text-xs text-ink-3">
                                                    No projects yet. Create one to build a pipeline.
                                                </p>
                                            ) : (
                                                state.projects.map((p) => (
                                                    <MenuRow
                                                        key={p.id}
                                                        icon={<FolderPlus className="w-4 h-4" />}
                                                        label={p.name}
                                                        onClick={() => {
                                                            const id = prism.ensureActiveSession();
                                                            prism.assignSessionToProject(id, p.id);
                                                            setPlusOpen(false);
                                                        }}
                                                    />
                                                ))
                                            )}
                                        </div>
                                        <div className="px-2 pt-1.5 pb-1">
                                            <div className="flex gap-1.5">
                                                <input
                                                    value={projectName}
                                                    onChange={(e) => setProjectName(e.target.value)}
                                                    placeholder="New project name"
                                                    className="flex-1 rounded-lg border border-[var(--border-hairline)] bg-surface-0 px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-accent-line"
                                                />
                                                <button
                                                    type="button"
                                                    disabled={!projectName.trim()}
                                                    onClick={() => {
                                                        const proj = addProject({ name: projectName.trim() });
                                                        const id = prism.ensureActiveSession();
                                                        prism.assignSessionToProject(id, proj.id);
                                                        setProjectName("");
                                                        setPlusOpen(false);
                                                    }}
                                                    className="rounded-lg bg-accent px-2.5 py-1.5 text-xs font-medium text-accent-ink disabled:opacity-40 hover:bg-accent-hover"
                                                >
                                                    Create
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ) : null}

                                {plusPage === "skills" ? (
                                    <div className="px-1">
                                        <SubHeader title="Skills" onBack={() => setPlusPage("root")} />
                                        <div className="max-h-60 overflow-y-auto">
                                            {builtinSkills.map((s) => (
                                                <MenuRow
                                                    key={s.id}
                                                    icon={<Sparkles className="w-4 h-4 text-accent" />}
                                                    label={s.name}
                                                    sublabel={s.description}
                                                    active={activeSkillId === s.id}
                                                    onClick={() => pickSkill(s.id, s.promptTemplate)}
                                                />
                                            ))}
                                            {customSkills.length ? (
                                                <p className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-ink-3">
                                                    Your skills
                                                </p>
                                            ) : null}
                                            {customSkills.map((s) => (
                                                <MenuRow
                                                    key={s.id}
                                                    icon={<Wand2 className="w-4 h-4 text-ok" />}
                                                    label={s.name}
                                                    sublabel={s.description}
                                                    active={activeSkillId === s.id}
                                                    onClick={() => pickSkill(s.id, s.promptTemplate)}
                                                />
                                            ))}
                                        </div>
                                        <div className="my-1 h-px bg-[var(--border-hairline)]" />
                                        <MenuRow
                                            icon={<Plus className="w-4 h-4" />}
                                            label="Create skill"
                                            onClick={() => {
                                                setSkillModalOpen(true);
                                                setPlusOpen(false);
                                            }}
                                        />
                                    </div>
                                ) : null}

                                {plusPage === "connectors" ? (
                                    <div className="px-1">
                                        <SubHeader title="Connectors" onBack={() => setPlusPage("root")} />
                                        <div className="max-h-60 overflow-y-auto">
                                            {catalog.connectors.filter((c) => c.configured).length === 0 ? (
                                                <p className="px-3 py-2 text-xs text-ink-3">
                                                    No connected connectors. Connect sources in Settings.
                                                </p>
                                            ) : (
                                                catalog.connectors
                                                    .filter((c) => c.configured)
                                                    .map((c) => {
                                                        const on = enabledConnectorIds.includes(c.id);
                                                        return (
                                                            <div
                                                                key={c.id}
                                                                className="flex items-center gap-2.5 px-3 py-2"
                                                            >
                                                                <Plug className="w-4 h-4 text-ink-3 shrink-0" />
                                                                <div className="min-w-0 flex-1">
                                                                    <p className="text-ink-0 truncate">{c.name}</p>
                                                                    <p className="text-xs text-ink-3 truncate">
                                                                        {c.description}
                                                                    </p>
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    role="switch"
                                                                    aria-checked={on}
                                                                    data-testid={`prism-connector-toggle-${c.id}`}
                                                                    onClick={() => {
                                                                        const next = on
                                                                            ? enabledConnectorIds.filter((id) => id !== c.id)
                                                                            : [...enabledConnectorIds, c.id];
                                                                        onEnabledConnectorIdsChange(next);
                                                                    }}
                                                                    className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${
                                                                        on ? "bg-accent" : "bg-surface-2"
                                                                    }`}
                                                                >
                                                                    <span
                                                                        className={`absolute top-0.5 h-5 w-5 rounded-full bg-card shadow transition-transform ${
                                                                            on ? "translate-x-5" : "translate-x-0.5"
                                                                        }`}
                                                                    />
                                                                </button>
                                                            </div>
                                                        );
                                                    })
                                            )}
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                    </div>

                    </div>

                    {/* Right controls: model selector + send */}
                    <div className="flex items-center gap-1.5">
                        <div className="relative" ref={modelRef}>
                            <button
                                type="button"
                                onClick={() => setModelOpen((o) => !o)}
                                className="flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs font-medium text-ink-2 hover:bg-surface-1 hover:text-ink-0 transition-colors"
                            >
                                {selectedModel?.label ?? "Model"}
                                <ChevronDown className="w-3.5 h-3.5" />
                            </button>
                            {modelOpen ? (
                                <div className="absolute bottom-10 right-0 z-30 w-64 rounded-2xl border border-[var(--border-hairline)] bg-surface-0 shadow-xl py-1.5 text-sm">
                                    <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-ink-3">
                                        Azure AI Foundry
                                    </p>
                                    {catalog.models.map((m) => (
                                        <button
                                            key={m.id}
                                            type="button"
                                            disabled={!m.available}
                                            onClick={() => {
                                                setModel(m.id as PrismModelId);
                                                setModelOpen(false);
                                            }}
                                            className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-surface-1 disabled:opacity-40 disabled:hover:bg-transparent"
                                        >
                                            <span className="w-4 pt-0.5">
                                                {state.selectedModelId === m.id ? (
                                                    <Check className="w-4 h-4 text-accent" />
                                                ) : null}
                                            </span>
                                            <span className="min-w-0">
                                                <span className="block text-ink-0">
                                                    {m.label}
                                                    {!m.available ? (
                                                        <span className="ml-1 text-[10px] text-ink-3">
                                                            (unavailable)
                                                        </span>
                                                    ) : null}
                                                </span>
                                                <span className="block text-xs text-ink-3">
                                                    {m.description}
                                                    {m.deployment ? ` · ${m.deployment}` : ""}
                                                </span>
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            ) : null}
                        </div>

                        {speech.available ? (
                            <PrismSpeechMicButton
                                listening={speech.listening}
                                audioLevels={speech.audioLevels}
                                disabled={loading}
                                onToggle={speech.toggleListening}
                            />
                        ) : null}

                        <button
                            type="button"
                            onClick={submit}
                            disabled={!canSend}
                            className="flex h-9 w-9 items-center justify-center rounded-full bg-accent text-accent-ink transition-colors hover:bg-accent-hover disabled:bg-surface-2 disabled:text-ink-3"
                            aria-label="Send message"
                        >
                            <ArrowUp className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </div>

            {previewAttachment?.previewUrl ? (
                <div
                    className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 p-4"
                    onClick={() => setPreviewAttachment(null)}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Image preview"
                >
                    <div
                        className="relative max-h-[90vh] max-w-[90vw] rounded-xl bg-surface-0 p-2 shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            onClick={() => setPreviewAttachment(null)}
                            className="absolute right-2 top-2 z-10 rounded-full bg-black/50 p-1.5 text-white hover:bg-black/70"
                            aria-label="Close preview"
                        >
                            <X className="w-4 h-4" />
                        </button>
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img
                            src={previewAttachment.previewUrl}
                            alt={previewAttachment.name}
                            className="max-h-[85vh] max-w-[85vw] rounded-lg object-contain"
                        />
                        <p className="mt-2 px-1 text-center text-xs text-ink-2">
                            {previewAttachment.name} · sent with your message to the model
                        </p>
                    </div>
                </div>
            ) : null}

            {skillModalOpen ? (
                <CreateSkillModal
                    onClose={() => setSkillModalOpen(false)}
                    onCreate={(skill) => {
                        const created = addCustomSkill(skill);
                        onActiveSkillChange(created.id);
                        onValueChange(created.promptTemplate);
                        setSkillModalOpen(false);
                    }}
                />
            ) : null}
        </div>
    );
}

function MenuRow({
    icon,
    label,
    sublabel,
    hint,
    chevron,
    active,
    onClick,
}: {
    icon: React.ReactNode;
    label: string;
    sublabel?: string;
    hint?: string;
    chevron?: boolean;
    active?: boolean;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="flex w-full items-center gap-2.5 px-3 py-2 text-left hover:bg-surface-1 transition-colors"
        >
            <span className="text-ink-2 shrink-0">{icon}</span>
            <span className="min-w-0 flex-1">
                <span className="block text-ink-0 truncate">{label}</span>
                {sublabel ? (
                    <span className="block text-xs text-ink-3 truncate">{sublabel}</span>
                ) : null}
            </span>
            {hint ? <span className="text-xs text-ink-3 shrink-0">{hint}</span> : null}
            {active ? <Check className="w-4 h-4 text-accent shrink-0" /> : null}
            {chevron ? <span className="text-ink-3 shrink-0">›</span> : null}
        </button>
    );
}

function SubHeader({ title, onBack }: { title: string; onBack: () => void }) {
    return (
        <div className="flex items-center gap-2 px-2 py-1.5">
            <button
                type="button"
                onClick={onBack}
                className="flex h-6 w-6 items-center justify-center rounded-md text-ink-2 hover:bg-surface-1"
                aria-label="Back"
            >
                <ArrowLeft className="w-4 h-4" />
            </button>
            <span className="text-sm font-semibold text-ink-0">{title}</span>
        </div>
    );
}

function CreateSkillModal({
    onClose,
    onCreate,
}: {
    onClose: () => void;
    onCreate: (skill: { name: string; description: string; promptTemplate: string }) => void;
}) {
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [promptTemplate, setPromptTemplate] = useState("");
    const valid = name.trim() && promptTemplate.trim();

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
            onClick={onClose}
        >
            <div
                className="w-full max-w-md rounded-2xl border border-[var(--border-hairline)] bg-surface-0 p-5 shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-base font-semibold text-ink-0">Create skill</h3>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-ink-3 hover:text-ink-0"
                        aria-label="Close"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <p className="text-xs text-ink-2 mb-4">
                    Save a reusable prompt that runs on Prism&apos;s live knowledge layer. Use{" "}
                    <code className="rounded bg-surface-1 px-1">{"{company}"}</code> to inject the
                    focused company.
                </p>
                <div className="space-y-3">
                    <label className="block">
                        <span className="text-xs font-medium text-ink-2">Name</span>
                        <input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g. Board pack summary"
                            className="mt-1 w-full rounded-lg border border-[var(--border-hairline)] bg-surface-0 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-line"
                        />
                    </label>
                    <label className="block">
                        <span className="text-xs font-medium text-ink-2">Description</span>
                        <input
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="What does this skill do?"
                            className="mt-1 w-full rounded-lg border border-[var(--border-hairline)] bg-surface-0 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-line"
                        />
                    </label>
                    <label className="block">
                        <span className="text-xs font-medium text-ink-2">Prompt</span>
                        <textarea
                            value={promptTemplate}
                            onChange={(e) => setPromptTemplate(e.target.value)}
                            rows={3}
                            placeholder="Summarize the latest board pack for {company} and list the top 3 risks."
                            className="mt-1 w-full resize-none rounded-lg border border-[var(--border-hairline)] bg-surface-0 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-line"
                        />
                    </label>
                </div>
                <div className="mt-5 flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-lg px-3 py-2 text-sm text-ink-2 hover:bg-surface-1"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        disabled={!valid}
                        onClick={() =>
                            onCreate({
                                name: name.trim(),
                                description: description.trim() || "Custom skill",
                                promptTemplate: promptTemplate.trim(),
                            })
                        }
                        className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-ink disabled:opacity-40 hover:bg-accent-hover"
                    >
                        Create skill
                    </button>
                </div>
            </div>
        </div>
    );
}

